import React, { FC, useCallback } from 'react'
import styles from 'styles/components/calendar.module.scss'
import getDate from 'date-fns/getDate'
import isSameDay from 'date-fns/isSameDay'
import isSameMonth from 'date-fns/isSameMonth'
import { useDispatch } from 'react-redux'
import { closeCautionModal, openCautionModal } from 'reducks/modal/operations'

type Props = {
  currentMonth: Date
  date: Date
  isActive: boolean
  isBefore: boolean
  isClosed: boolean
  isOrdered: boolean
  isSet: boolean
  isSpecialHoliday: boolean
  selectDate: (date: Date) => void
}

const ScheduleCalendarCell: FC<Props> = React.memo((props) => {
  const { currentMonth, date, isActive, isBefore, isClosed, isOrdered, isSet, isSpecialHoliday, selectDate } = props

  const dispatch = useDispatch()

  const openIndividualSettingModal = useCallback(() => {
    dispatch(
      openCautionModal(
        '停止する',
        'キャンセル',
        '選択された日程は、個別の設定がされています。受渡可能日から除外してもよろしいですか？',
        () => {
          selectDate(date)
          dispatch(closeCautionModal())
        },
        '個別設定されています'
      )
    )
  }, [selectDate, date])

  const openHolidayModal = useCallback(() => {
    dispatch(
      openCautionModal(
        '',
        '戻る',
        '選択された日付は、休業日の為、受渡可能日に設定することが出来ません。',
        () => dispatch(closeCautionModal()),
        '休業日に設定されています'
      )
    )
  }, [selectDate, date])

  const isThisMonth = isSameMonth(date, currentMonth)

  const className = (() => {
    switch (true) {
      // 当日以前の休業日が祝日
      case isThisMonth && isClosed && isBefore && isSpecialHoliday:
        return styles.table__cell__holiday_disabled
      // 当日以前の休業日
      case isThisMonth && isClosed && isBefore:
        return styles.table__cell_disabled
      // 当日以前の営業日が祝日
      case isThisMonth && !isClosed && isBefore && isSpecialHoliday:
        return styles.table__cell__holiday_before
      // 当日以前の営業日
      case isThisMonth && !isClosed && isBefore:
        return styles.table__cell_before
      // 個別設定日
      case isThisMonth && isSet:
        return styles.table__cell_set
      // 選択状態
      case isThisMonth && isActive:
        return styles.table__cell_active
      // 当日以降の休業日・臨時休業日が祝日
      case isThisMonth && isClosed && isSpecialHoliday:
        return styles.table__cell__holiday_disabled
      // 当日以降の休業日・臨時休業日
      case isThisMonth && isClosed:
        return styles.table__cell_disabled
      // 当日が祝日
      case isThisMonth && isSameDay(date, new Date()) && isSpecialHoliday:
        return styles.table__cell__holiday_today
      // 今日の日付
      case isThisMonth && isSameDay(date, new Date()):
        return styles.table__cell_today
      case isThisMonth && isSpecialHoliday:
        return styles.table__cell__holiday
      default:
        return styles.table__cell
    }
  })()

  const onClickDate = () => {
    if (!isThisMonth || isBefore) {
      return null
    } else if (isSet) {
      openIndividualSettingModal()
    } else if (isClosed) {
      openHolidayModal()
    } else {
      selectDate(date)
    }
  }

  return (
    <td className={className} onClick={onClickDate}>
      <span className={styles.table__cell__date}>
        {isThisMonth ? getDate(date) : ''}
        {isThisMonth && !isBefore && (isActive || isSet) && isOrdered && <span className={styles.table__mark_white} />}
        {isThisMonth && !isBefore && !isActive && !isSet && isOrdered && <span className={styles.table__mark} />}
      </span>
    </td>
  )
})

export default ScheduleCalendarCell
